import React, { useState } from 'react';
import { Container } from '../../styles/Styles';
import GuideNav from './GuideNav';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { useAuth } from '../../context/AuthContext';
import { Roles } from '../common/roles';
import { Navigate } from 'react-router-dom';
import TimeRangePicker from '../common/TimeRangePicker';
import { useMutation, useQuery } from '@apollo/client';
import { UPDATE_GUIDE_AVAILABILITY, GET_AVAILABILITY } from '../gql/guides-gql';
import { toast } from 'react-toastify';
import { InfinitySpin } from 'react-loader-spinner';

const DnDCalendar = withDragAndDrop(Calendar);
const localizer = momentLocalizer(moment);

interface AvailabilityEvent {
    id: string;
    title: string;
    start: Date;
    end: Date;
    isAvailable: boolean;
}

interface ScheduleItem {
    day: Date;
    startTime: Date;
    endTime: Date;
}

const GuideAvailability = () => {
    const { user, isAuthenticated } = useAuth();
    const [events, setEvents] = useState<AvailabilityEvent[]>([]);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [showTimePicker, setShowTimePicker] = useState(false);

    const { data: availabilityData, loading, error } = useQuery(GET_AVAILABILITY, {
        onError: (error) => {
            toast.error('Failed to load availability data. Please try again later.');
            console.error('Error loading availability:', error);
        }
    });

    const [updateAvailability] = useMutation(UPDATE_GUIDE_AVAILABILITY, {
        refetchQueries: [{query: GET_AVAILABILITY}],
        onCompleted: () => {
            toast.success('Availability updated successfully');
        },
        onError: (error) => {
            toast.error(`Error updating availability: ${error.message}`);
        }
    });

    // Initialize calendar with availability data
    React.useEffect(() => {
        const today = moment().startOf('day');
        const threeMonthsLater = moment().add(3, 'months').endOf('day');
        const days: AvailabilityEvent[] = [];

        // Create a map of available days from the schedule
        const availableDays = new Map<string, { startTime: Date; endTime: Date }>();

        // Process all availability entries
        if (availabilityData?.getAvailability) {
            availabilityData.getAvailability.forEach((availability: any) => {
                if (availability.schedule) {
                    availability.schedule.forEach((item: ScheduleItem) => {

                        const dayId = moment.utc(item.day).format('YYYY-MM-DD');

                        // Parse the times directly from the UTC strings
                        const startTime = moment(item.startTime).toDate();
                        const endTime = moment(item.endTime).toDate();

                        availableDays.set(dayId, {
                            startTime,
                            endTime
                        });
                    });
                }
            });
        }

        // Generate events for each day
        let currentDate = today.clone();
        while (currentDate.isBefore(threeMonthsLater)) {
            const dayId = currentDate.format('YYYY-MM-DD');
            const availableDay = availableDays.get(dayId);

            if (availableDay) {
                // Day is available
                const event = {
                    id: dayId,
                    title: `Available: ${moment.utc(availableDay.startTime).format('HH:mm')} - ${moment.utc(availableDay.endTime).format('HH:mm')}`,
                    start: availableDay.startTime,
                    end: availableDay.endTime,
                    isAvailable: true
                };

                days.push(event);
            } else {
                // Day is not available
                days.push({
                    id: dayId,
                    title: 'Not Available',
                    start: currentDate.toDate(),
                    end: currentDate.clone().endOf('day').toDate(),
                    isAvailable: false
                });
            }
            currentDate.add(1, 'day');
        }

        setEvents(days);
    }, [availabilityData]);

    const updateEventForDate = (dateId: string, newEvent: AvailabilityEvent) => {
        setEvents(prevEvents => {
            const filteredEvents = prevEvents.filter(event => event.id !== dateId);
            return [...filteredEvents, newEvent];
        });
    };

    if (!isAuthenticated || user?.role[0] !== Roles.GUIDE) {
        return <Navigate to="/" />;
    }

    if (loading) {
        return (
            <Container>
                <GuideNav />
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                    <InfinitySpin width="200" color="#4CAF50" />
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container>
                <GuideNav />
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <h2>Error Loading Availability</h2>
                    <p>Unable to connect to the server. Please try again later.</p>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Retry
                    </button>
                </div>
            </Container>
        );
    }

    const handleSelectSlot = (slotInfo: { start: Date; end: Date }) => {
        const threeMonthsLater = moment().add(3, 'months').endOf('day');
        const selectedMoment = moment(slotInfo.start);
        const today = moment().startOf('day');

        // Don't allow selection of dates beyond 3 months or in the past
        if (selectedMoment.isAfter(threeMonthsLater) || selectedMoment.isBefore(today)) {
            toast.error('You can only select dates from today up to 3 months in the future');
            return;
        }

        setSelectedDate(slotInfo.start);
        setShowTimePicker(true);
    };

    const handleTimeSelect = (startTime: string, endTime: string) => {
        if (!selectedDate) return;


        const start = moment(startTime, 'HH:mm');
        const end = moment(endTime, 'HH:mm');

        // Check if end time is before start time (crossing midnight)
        if (end.isBefore(start)) {
            toast.error('End time cannot be before start time');
            return;
        }

        // Check if the time range is at least 1 hour
        const duration = moment.duration(end.diff(start));
        if (duration.asHours() < 1) {
            toast.error('Time range must be at least 1 hour');
            return;
        }

        const dateId = moment(selectedDate).format('YYYY-MM-DD');

        // Create the event times in local timezone
        const eventStart = moment(selectedDate)
            .set({ hour: parseInt(startTime.split(':')[0]), minute: parseInt(startTime.split(':')[1]) })
            .toDate();
        const eventEnd = moment(selectedDate)
            .set({ hour: parseInt(endTime.split(':')[0]), minute: parseInt(endTime.split(':')[1]) })
            .toDate();

        const newEvent = {
            id: dateId,
            title: `Available: ${startTime} - ${endTime}`,
            start: eventStart,
            end: eventEnd,
            isAvailable: true
        };

        setEvents(prevEvents => {
            const filteredEvents = prevEvents.filter(event => event.id !== dateId);
            return [...filteredEvents, newEvent];
        });

        setShowTimePicker(false);
        setSelectedDate(null);

        // Create full datetime strings for the backend
        const startDateTime = moment(selectedDate)
            .set({ hour: parseInt(startTime.split(':')[0]), minute: parseInt(startTime.split(':')[1]) })
            .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

        const endDateTime = moment(selectedDate)
            .set({ hour: parseInt(endTime.split(':')[0]), minute: parseInt(endTime.split(':')[1]) })
            .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');


        // Submit the availability
        updateAvailability({
            variables: {
                availabilityInput: {
                    guide: user?.id,
                    monthYear: moment(selectedDate).format('YYYY-MM'),
                    day: moment(selectedDate).format('YYYY-MM-DD'),
                    schedule: [{
                        day: moment(selectedDate).format('YYYY-MM-DD'),
                        startTime: startDateTime,
                        endTime: endDateTime
                    }]
                }
            }
        });
    };

    const handleMarkUnavailable = async () => {
        if (!selectedDate || !user) return;

        const selectedMoment = moment(selectedDate);
        const monthYear = selectedDate;

        try {
            await updateAvailability({
                variables: {
                    availabilityInput: {
                        guide: user.id,
                        monthYear,
                        day: moment(selectedDate).format('YYYY-MM-DD'),
                        schedule: []
                    }
                }
            });

            const dateId = selectedMoment.format('YYYY-MM-DD');
            const newEvent: AvailabilityEvent = {
                id: dateId,
                title: 'Not Available',
                start: selectedMoment.startOf('day').toDate(),
                end: selectedMoment.endOf('day').toDate(),
                isAvailable: false
            };

            updateEventForDate(dateId, newEvent);
            setShowTimePicker(false);
        } catch (error) {
            console.error('Error updating availability:', error);
        }
    };

    return (
        <Container>
            <GuideNav />
            <h2>Availability Calendar</h2>
            <div style={{
                width: '100%',
                overflow: 'hidden'
            }}>
                <div style={{
                    overflowX: 'auto',
                    paddingBottom: '20px'
                }}>
                    <div style={{
                        minWidth: '640px',
                        height: '80vh'
                    }}>
                        <DnDCalendar
                            localizer={localizer}
                            events={events}
                            startAccessor={(event: any) => new Date(event.start)}
                            endAccessor={(event: any) => new Date(event.end)}
                            selectable
                            longPressThreshold={0}
                            onSelectSlot={handleSelectSlot}
                            eventPropGetter={(event: any) => ({
                                style: {
                                    backgroundColor: event.isAvailable ? '#4CAF50' : '#f44336',
                                    borderRadius: '4px',
                                    opacity: 0.8,
                                    color: 'white',
                                    display: 'block',
                                    cursor: 'default',
                                    height: '100%',
                                    width: '100%'
                                }
                            })}
                            defaultView="month"
                            views={['month', 'week', 'day']}
                            min={new Date(0, 0, 0, 8, 0, 0)}
                            max={new Date(0, 0, 0, 20, 0, 0)}
                            components={{
                                event: ({ event }: { event: any }) => (
                                    <div style={{
                                        cursor: 'default',
                                        fontSize: '0.85rem',
                                        padding: '2px 4px'
                                    }}>
                                        {event.title}
                                    </div>
                                ),
                                dateCellWrapper: ({ children }: { children: React.ReactNode }) => (
                                    <div style={{
                                        cursor: 'pointer',
                                        height: '100%',
                                        width: '100%',
                                        position: 'relative'
                                    }}>
                                        {children}
                                    </div>
                                )
                            }}
                            dayPropGetter={() => ({
                                style: {
                                    cursor: 'pointer',
                                    height: '100%',
                                    width: '100%',
                                    border: '1px solid #ddd'
                                }
                            })}
                        />
                    </div>
                </div>
            </div>
            {showTimePicker && selectedDate && user && (
                <TimeRangePicker
                    onSelect={handleTimeSelect}
                    onClose={() => setShowTimePicker(false)}
                    onUnavailable={handleMarkUnavailable}
                    selectedDate={selectedDate}
                    guideId={user.id}
                />
            )}
        </Container>
    );
};

export default GuideAvailability;
